/**
 * Action Button Thunks
 *
 * Thunk for fetching template data when user clicks an action-button.
 * Calls the endpoint from the button's action contract and sets the active template.
 * Shows FlightsPopup on API errors.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchActionButtonTemplate, ActionButtonApiError } from '../api/actionButtonApi';
import { setActiveTemplate } from '../slices/templateSlice';
import { showFlightsErrorPopup } from '@/features/flights/redux';
import { abortCurrentPoll } from '../../utils/streamAbortManager';
import { logger } from '@/shared/utils/logger';
import type { AppDispatch } from '@/store/store';

interface FetchActionButtonTemplateParams {
  method: 'GET' | 'POST';
  params?: Record<string, string>;
}

export const fetchActionButtonTemplateThunk = createAsyncThunk<
  void,
  FetchActionButtonTemplateParams,
  { dispatch: AppDispatch }
>('chat/fetchActionButtonTemplate', async ({ method, params }, { dispatch }) => {
  try {
    abortCurrentPoll();
    const apiResponse = await fetchActionButtonTemplate(method, params);
    const assistantMessage = apiResponse.response;
    dispatch(
      setActiveTemplate({
        template: assistantMessage.activeTemplate ?? null,
        data: assistantMessage.templateData ?? null,
      }),
    );
  } catch (error: unknown) {
    logger.error('[fetchActionButtonTemplate] Failed:', error);
    if (error instanceof ActionButtonApiError) {
      dispatch(
        showFlightsErrorPopup({
          errorMessage: error.errorMessage,
          errorDescription: error.errorDescription,
        }),
      );
    }
  }
});
